/**
 * Segmented — the app's toggle control (research: segmented controls beat chip
 * rows for small mutually-exclusive sets; radios/checkboxes for rails). One
 * sliding-highlight container, full keyboard/focus support via real buttons.
 */

export function Segmented<T extends string>({
  options,
  value,
  onChange,
  size = "md",
  ariaLabel,
}: {
  options: Array<{ value: T; label: string; disabled?: boolean }>;
  value: T;
  onChange: (v: T) => void;
  size?: "sm" | "md";
  ariaLabel?: string;
}): React.ReactElement {
  const pad = size === "sm" ? "4px 10px" : "6px 13px";
  const font = size === "sm" ? 12 : 12.5;
  return (
    <div role="group" aria-label={ariaLabel} style={wrap}>
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            onClick={() => !o.disabled && onChange(o.value)}
            disabled={o.disabled}
            aria-pressed={on}
            style={{
              border: "none",
              borderRadius: 7,
              padding: pad,
              fontSize: font,
              fontWeight: 600,
              cursor: o.disabled ? "not-allowed" : "pointer",
              opacity: o.disabled ? 0.4 : 1,
              background: on ? "var(--surface)" : "transparent",
              color: on ? "var(--text)" : "var(--body)",
              boxShadow: on ? "0 1px 4px rgba(0,0,0,0.18), 0 0 0 1px var(--border)" : "none",
              whiteSpace: "nowrap",
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

const wrap: React.CSSProperties = {
  display: "inline-flex",
  flexWrap: "wrap",
  maxWidth: "100%",
  gap: 2,
  background: "var(--surface-2)",
  border: "1px solid var(--border)",
  borderRadius: 9,
  padding: 2,
};
